import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: object;
  keywords?: string;
}

const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = "https://luminate.media/og-image.jpg",
  ogType = "website",
  jsonLd,
  keywords,
}: SEOHeadProps) => {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", description);
    }

    // Update keywords if provided
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement("meta");
        metaKeywords.setAttribute("name", "keywords");
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute("content", keywords);
    }

    // Update OG tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute("content", title);
    }

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) {
      ogDesc.setAttribute("content", description);
    }

    let ogImageTag = document.querySelector('meta[property="og:image"]');
    if (ogImageTag) {
      ogImageTag.setAttribute("content", ogImage);
    }

    let ogTypeTag = document.querySelector('meta[property="og:type"]');
    if (ogTypeTag) {
      ogTypeTag.setAttribute("content", ogType);
    }

    // Handle canonical
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!canonicalTag) {
        canonicalTag = document.createElement("link");
        canonicalTag.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalTag);
      }
      canonicalTag.setAttribute("href", canonical);
    }

    // Handle JSON-LD
    const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
    if (jsonLd) {
      if (existingJsonLd) {
        existingJsonLd.textContent = JSON.stringify(jsonLd);
      } else {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(script);
      }
    }

    return () => {
      // Cleanup JSON-LD on unmount if we added it
      if (jsonLd) {
        const script = document.querySelector('script[type="application/ld+json"]');
        if (script) {
          script.remove();
        }
      }
    };
  }, [title, description, canonical, ogImage, ogType, jsonLd, keywords]);

  return null;
};

export default SEOHead;

// Schema generators for different page types
export const generateLocalBusinessSchema = (stateName?: string) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://luminate.media",
  name: "Luminate Media",
  image: "https://luminate.media/og-image.jpg",
  url: "https://luminate.media",
  telephone: "+1-360-200-8922",
  email: "team@luminate.media",
  description: "Professional real estate photography, video, drone, and virtual tour services.",
  address: {
    "@type": "PostalAddress",
    addressRegion: stateName || "AR",
    addressCountry: "US",
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "08:00",
    closes: "18:00",
  },
  sameAs: [
    "https://www.facebook.com/luminatemedia",
    "https://www.instagram.com/luminatemedia",
    "https://www.youtube.com/luminatemedia",
  ],
  priceRange: "$$",
  areaServed: [
    { "@type": "State", name: "Washington" },
    { "@type": "State", name: "Arkansas" },
  ],
});

export const generateServiceSchema = (serviceName: string, description: string) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: serviceName,
  provider: {
    "@type": "LocalBusiness",
    name: "Luminate Media",
    url: "https://luminate.media",
  },
  description: description,
  areaServed: [
    { "@type": "State", name: "Washington" },
    { "@type": "State", name: "Arkansas" },
  ],
});

export const generateLocationSchema = (stateName: string, stateAbbrev: string, cities: string[]) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `https://luminate.media/${stateName.toLowerCase()}`,
  name: `Luminate Media - ${stateName}`,
  url: `https://luminate.media/${stateName.toLowerCase()}`,
  telephone: "+1-360-200-8922",
  description: `Professional real estate photography and video services in ${stateName}.`,
  address: {
    "@type": "PostalAddress",
    addressRegion: stateAbbrev,
    addressCountry: "US",
  },
  areaServed: cities.map((city) => ({
    "@type": "City",
    name: city,
    containedInPlace: { "@type": "State", name: stateName },
  })),
  parentOrganization: {
    "@type": "Organization",
    name: "Luminate Media",
    url: "https://luminate.media",
  },
});

export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

export const generateSpeakableSchema = (url: string) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "url": url,
  "speakable": {
    "@type": "SpeakableSpecification",
    "cssSelector": ["#city-intro", "#city-faqs", "h1", ".speakable"],
  },
});

export const generateHowToSchema = (name: string, steps: { name: string; text: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  step: steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.name,
    text: s.text,
  })),
});

export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});
